import { useMutation, useQuery } from '@tanstack/react-query'
import { guardEncounterWrite } from '@util'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { GeneralResMock } from '../types/general.type'
import { NoteIncluded, NoteResMock } from '../types/note.type'

type Props = {
  id?: string
  patientId?: string
  // Any of these categories (sent as category[]).
  category?: string[]
  // None of these (notes without a category are kept).
  excludeCategory?: string[]
  // Page size (backend default 20, at most 100).
  size?: number
  note_type?: string
  encounterId?: string
  created_by_me?: string
  // false: skip the notes list (screens that only need the single note)
  list?: boolean
}

export const useNote = ({
  category,
  created_by_me,
  encounterId,
  excludeCategory,
  id,
  list = true,
  note_type,
  patientId,
  size
}: Props = {}) => {
  const notable_type = 'Patient'

  const {
    data: notes,
    isFetching: notesIsFetching,
    isPlaceholderData: notesIsPlaceholderData,
    isLoading: notesIsLoading
  } = useQuery({
    // No placeholder data: another encounter's/patient's notes must never show while this list loads.
    enabled: list,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<NoteResMock[], NoteIncluded[]>>(`notes`, {
        params: {
          ...(category?.length && { category }),
          ...(excludeCategory?.length && { exclude_category: excludeCategory }),
          ...(created_by_me && { created_by_me }),
          ...(encounterId && { encounter_id: encounterId }),
          ...(size && { size }),
          ...(note_type && { note_type }),
          ...(patientId && { notable_id: patientId }),
          ...(notable_type && { notable_type })
        }
      })
      return data
    },
    queryKey: ['note', { category, created_by_me, encounterId, excludeCategory, note_type, patientId, size }]
  })

  const {
    data: singleNote,
    isLoading: noteIsLoading,
    refetch: refetchSingleNote
  } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<NoteResMock, NoteIncluded[]>>(`notes/${id}`)
      return data
    },
    queryKey: ['note', id]
  })

  const { mutateAsync: createNote } = useMutation({
    mutationFn: async (formVal: any) => {
      if (!guardEncounterWrite()) throw new Error('Encounter is completed')
      const { data } = await AXIOS_INSTANCE.post<GeneralResMock<NoteResMock>>('notes', formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note'] })
    }
  })

  const { mutateAsync: updateNote } = useMutation({
    mutationFn: async ({ data, id }: { data: any; id: string }) => {
      if (!guardEncounterWrite()) throw new Error('Encounter is completed')
      await AXIOS_INSTANCE.patch(`notes/${id}`, data, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note'] })
    }
  })

  const { mutateAsync: deleteNote } = useMutation({
    mutationFn: async (id: string) => {
      if (!guardEncounterWrite()) throw new Error('Encounter is completed')
      await AXIOS_INSTANCE.delete(`notes/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['note'] })
    }
  })

  return {
    createNote,
    deleteNote,
    includedDatas: notes?.included,
    notesPagination: notes?.meta?.pagination,
    noteIsLoading,
    notes: notes?.data,
    notesIsFetching,
    notesIsLoading,
    notesIsPlaceholderData,
    refetchSingleNote,
    singleNote,
    updateNote
  }
}
